type BrandingThemeInput = {
  colorPrimario?: string | null;
  colorSecundario?: string | null;
  temaOscuro?: boolean | null;
  colorFondoClaro?: string | null;
  colorFondoOscuro?: string | null;
  colorSuperficieClaro?: string | null;
};

const DEFAULT_PRIMARY = '#314a25';
const DEFAULT_SECONDARY = '#bfa98d';
const DEFAULT_BG_LIGHT = '#f3f0ea';
const DEFAULT_BG_DARK = '#0b1220';
const DEFAULT_SURFACE_LIGHT = '#ffffff';

const isValidHex = (value: string) => /^#[0-9a-fA-F]{6}$/.test(value);

const normalizeHex = (value: string | null | undefined) => {
  const v = String(value ?? '').trim();
  return isValidHex(v) ? v.toLowerCase() : null;
};

const hexToRgb = (hex: string) => {
  const raw = hex.replace('#', '');
  const r = parseInt(raw.slice(0, 2), 16);
  const g = parseInt(raw.slice(2, 4), 16);
  const b = parseInt(raw.slice(4, 6), 16);
  return { r, g, b };
};

const rgbToHex = (r: number, g: number, b: number) => {
  const to = (n: number) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0');
  return `#${to(r)}${to(g)}${to(b)}`;
};

const mix = (a: { r: number; g: number; b: number }, b: { r: number; g: number; b: number }, t: number) => {
  const tt = Math.max(0, Math.min(1, t));
  return {
    r: a.r + (b.r - a.r) * tt,
    g: a.g + (b.g - a.g) * tt,
    b: a.b + (b.b - a.b) * tt,
  };
};

const lighten = (rgb: { r: number; g: number; b: number }, amount: number) => {
  return mix(rgb, { r: 255, g: 255, b: 255 }, amount);
};

const darken = (rgb: { r: number; g: number; b: number }, amount: number) => {
  return mix(rgb, { r: 0, g: 0, b: 0 }, amount);
};

const relativeLuminance = (rgb: { r: number; g: number; b: number }) => {
  const linear = [rgb.r, rgb.g, rgb.b].map((channel) => {
    const value = channel / 255;
    return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * linear[0] + 0.7152 * linear[1] + 0.0722 * linear[2];
};

const contrastRatio = (a: number, b: number) => {
  const lighter = Math.max(a, b);
  const darker = Math.min(a, b);
  return (lighter + 0.05) / (darker + 0.05);
};

export const getContrastText = (background: string) => {
  const normalized = normalizeHex(background) ?? '#ffffff';
  const luminance = relativeLuminance(hexToRgb(normalized));
  const whiteLuminance = relativeLuminance(hexToRgb('#f8fafc'));
  const darkLuminance = relativeLuminance(hexToRgb('#0f172a'));
  return contrastRatio(luminance, whiteLuminance) >= contrastRatio(luminance, darkLuminance)
    ? '#f8fafc'
    : '#0f172a';
};

export function applyBrandingTheme(input: BrandingThemeInput) {
  if (typeof document === 'undefined') return;

  const primary = normalizeHex(input.colorPrimario) ?? DEFAULT_PRIMARY;
  const secondary = normalizeHex(input.colorSecundario) ?? DEFAULT_SECONDARY;
  const bgLight = normalizeHex(input.colorFondoClaro) ?? DEFAULT_BG_LIGHT;
  const bgDark = normalizeHex(input.colorFondoOscuro) ?? DEFAULT_BG_DARK;
  const surfaceLight = normalizeHex(input.colorSuperficieClaro) ?? DEFAULT_SURFACE_LIGHT;
  const dark = !!input.temaOscuro;
  const activeBackground = dark ? bgDark : bgLight;
  // A dark configured background must work even if the user forgot to enable the
  // theme switch. This is especially important for tenant-specific branding.
  const automaticDarkContrast = getContrastText(activeBackground) === '#f8fafc';
  const activeSurface = automaticDarkContrast
    ? lighten(hexToRgb(activeBackground), 0.055)
    : hexToRgb(surfaceLight);
  const surfaceHex = rgbToHex(activeSurface.r, activeSurface.g, activeSurface.b);
  const surface2 = automaticDarkContrast ? lighten(activeSurface, 0.055) : darken(activeSurface, 0.055);
  const border = automaticDarkContrast ? lighten(activeSurface, 0.14) : darken(activeSurface, 0.14);
  const backgroundText = getContrastText(activeBackground);
  const surfaceText = getContrastText(surfaceHex);

  const root = document.documentElement;
  const p = hexToRgb(primary);
  const s = hexToRgb(secondary);
  const p50 = lighten(p, 0.92);
  const p100 = lighten(p, 0.84);
  const p400 = lighten(p, 0.12);
  const p600 = darken(p, 0.14);
  const p700 = darken(p, 0.26);
  const s600 = darken(s, 0.14);

  // Expose a small palette derived from the chosen colors so we can override Tailwind teal utilities.
  root.style.setProperty('--brand-primary', primary);
  root.style.setProperty('--brand-secondary', secondary);
  root.style.setProperty('--brand-primary-rgb', `${p.r} ${p.g} ${p.b}`);
  root.style.setProperty('--brand-secondary-rgb', `${s.r} ${s.g} ${s.b}`);

  root.style.setProperty('--brand-primary-50', rgbToHex(p50.r, p50.g, p50.b));
  root.style.setProperty('--brand-primary-100', rgbToHex(p100.r, p100.g, p100.b));
  root.style.setProperty('--brand-primary-400', rgbToHex(p400.r, p400.g, p400.b));
  root.style.setProperty('--brand-primary-500', primary);
  root.style.setProperty('--brand-primary-600', rgbToHex(p600.r, p600.g, p600.b));
  root.style.setProperty('--brand-primary-700', rgbToHex(p700.r, p700.g, p700.b));

  root.style.setProperty('--brand-secondary-500', secondary);
  root.style.setProperty('--brand-secondary-600', rgbToHex(s600.r, s600.g, s600.b));
  root.style.setProperty('--brand-primary-text', getContrastText(primary));
  root.style.setProperty('--brand-primary-600-text', getContrastText(rgbToHex(p600.r, p600.g, p600.b)));
  root.style.setProperty('--brand-primary-700-text', getContrastText(rgbToHex(p700.r, p700.g, p700.b)));
  root.style.setProperty('--brand-secondary-text', getContrastText(secondary));
  root.style.setProperty('--app-bg', activeBackground);
  root.style.setProperty('--app-bg-light', bgLight);
  root.style.setProperty('--app-bg-dark', bgDark);
  root.style.setProperty('--app-surface-light', surfaceLight);
  root.style.setProperty('--app-surface', surfaceHex);
  root.style.setProperty('--app-surface-2', rgbToHex(surface2.r, surface2.g, surface2.b));
  root.style.setProperty('--app-border', rgbToHex(border.r, border.g, border.b));
  root.style.setProperty('--app-text', surfaceText);
  root.style.setProperty('--app-text-2', surfaceText === '#f8fafc' ? '#cbd5e1' : '#334155');
  root.style.setProperty('--app-muted', surfaceText === '#f8fafc' ? '#94a3b8' : '#64748b');
  root.style.setProperty('--app-muted-2', surfaceText === '#f8fafc' ? '#64748b' : '#94a3b8');
  root.style.setProperty('--app-bg-text', backgroundText);

  // Contrast is based on the actual selected color, not only on the switch.
  root.classList.toggle('dark', automaticDarkContrast);
  // Helps native form controls align with the theme.
  root.style.setProperty('color-scheme', automaticDarkContrast ? 'dark' : 'light');
}
